import { TextInput, Select, Switch, Stack } from "@mantine/core";
import { useForm } from "@mantine/form";
import { zodResolver } from "mantine-form-zod-resolver";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import { UserCreate, UserUpdate, RoleSchema, ClearanceSchema, type User } from "@iso27001/shared";
import { usersApi } from "../../api/users.js";
import { ApiError } from "../../api/client.js";
import { useAuth } from "../../auth/AuthContext.js";
import { useEnumLabel } from "../../i18n/enum-label.js";
import { useApiErrorMessage } from "../../i18n/api-error.js";
import { EntityModal } from "../../components/EntityModal.js";

// ── Props ─────────────────────────────────────────────────────────────────────

interface Props {
    opened: boolean;
    onClose: () => void;
    initial: User | null;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function UserFormModal({ opened, onClose, initial }: Props) {
    const qc = useQueryClient();
    const isEdit = !!initial;
    const { t } = useTranslation();
    const { label } = useEnumLabel();
    const apiError = useApiErrorMessage();
    const { user: currentUser } = useAuth();
    // The API refuses to let an admin deactivate their own account, so offering
    // the control here would only produce a rejected save. The user list's own
    // toggle is disabled on your row for the same reason.
    const isSelf = isEdit && initial!.id === currentUser?.id;

    const roleOptions = RoleSchema.options.map((value) => ({ value, label: label("role", value) }));
    const clearanceOptions = ClearanceSchema.options.map((value) => ({ value, label: label("clearance", value) }));

    const form = useForm({
        initialValues: {
            email: initial?.email ?? "",
            name: initial?.name ?? "",
            role: initial?.role ?? "viewer",
            clearance: initial?.clearance ?? "intern",
            initials: initial?.initials ?? "",
            active: initial?.active ?? true,
        },
        validate: zodResolver(isEdit ? UserUpdate : UserCreate),
    });

    const mut = useMutation({
        mutationFn: async (vals: typeof form.values) => {
            const initials = vals.initials?.trim() || null;
            if (isEdit) {
                return usersApi.update(initial!.id, {
                    name: vals.name,
                    role: vals.role as User["role"],
                    clearance: vals.clearance as User["clearance"],
                    initials,
                    active: vals.active,
                });
            }
            return usersApi.create({
                email: vals.email,
                name: vals.name,
                role: vals.role as User["role"],
                clearance: vals.clearance as User["clearance"],
                initials,
                active: vals.active,
            });
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["users-all"] });
            notifications.show({
                message: isEdit ? t("users.form.notify.updated") : t("users.form.notify.created"),
                color: "teal",
            });
            onClose();
        },
        onError: (e) => {
            // Which Conflict this is depends entirely on the verb, and the two
            // must not be confused: POST's only conflict is a duplicate email,
            // while UserUpdate carries no email field at all, so PATCH's can
            // only be the last-admin guard. Pinning the latter to the email
            // input would have been invisible — that field is disabled on edit.
            if (e instanceof ApiError && e.code === "Conflict") {
                if (isEdit) {
                    notifications.show({ message: t("users.error.lastAdmin"), color: "red" });
                } else {
                    form.setFieldError("email", t("users.error.emailTaken"));
                }
                return;
            }
            notifications.show({ message: apiError(e, { fallback: "users.error.saveFailed" }), color: "red" });
        },
    });

    return (
        // Only admins reach this dialog (the page refuses everyone else), so it
        // is never read-only.
        <EntityModal
            opened={opened}
            onClose={onClose}
            size="md"
            title={isEdit ? t("users.form.titleEdit") : t("users.newUser")}
            readOnly={false}
            onSubmit={form.onSubmit((vals) => mut.mutate(vals))}
            saving={mut.isPending}
            submitLabel={isEdit ? t("common.action.save") : t("common.action.createSubmit")}
        >
            <Stack>
                <TextInput
                    label={t("users.field.email")}
                    required={!isEdit}
                    disabled={isEdit}
                    maxLength={200}
                    {...form.getInputProps("email")}
                />
                <TextInput
                    label={t("common.field.name")}
                    required
                    maxLength={200}
                    {...form.getInputProps("name")}
                />
                <Select
                    label={t("users.field.role")}
                    required
                    data={roleOptions}
                    {...form.getInputProps("role")}
                />
                <Select
                    label={t("users.field.clearance")}
                    description={t("users.form.clearanceHint")}
                    required
                    data={clearanceOptions}
                    {...form.getInputProps("clearance")}
                />
                <TextInput
                    label={t("users.field.initials")}
                    maxLength={20}
                    placeholder={t("users.form.initialsPlaceholder")}
                    {...form.getInputProps("initials")}
                />
                <Switch
                    label={t("users.field.active")}
                    description={isSelf ? t("users.form.activeSelfHint") : undefined}
                    disabled={isSelf}
                    checked={form.values.active}
                    onChange={(e) => form.setFieldValue("active", e.currentTarget.checked)}
                />
            </Stack>
        </EntityModal>
    );
}
